import * as React from 'react';
import {
  Box,
  DialogContent,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from '@mui/material';

import { useSelector, useDispatch } from 'react-redux'
import { getUser } from '../slices/auth';
import { addVisualizationGraph } from '../slices/visualization';
import { CDialog, CDialogTitle, CDialogActions, CButton, CTextField } from '../components/common/Modal';
import visualizationService from '../services/visualization';

export default function VisualizationModal(props) {
  const defaultDb = useSelector(state => state.auth.currentDB);
  const dbs = useSelector(state => state.auth.user.db);

  const {open, handleClose} = props;

  const [title, setTitle] = React.useState('New Visualization');
  const [prompt, setPrompt] = React.useState('');
  const [database, setDatabase] = React.useState(defaultDb);
  const [loading, setLoading] = React.useState(false);

  // Column selection state (step 2)
  const [step, setStep] = React.useState(1);
  const [columns, setColumns] = React.useState([]);
  const [xColumn, setXColumn] = React.useState([]);
  const [yColumn, setYColumn] = React.useState('');
  const [previewData, setPreviewData] = React.useState(null);

  const dispatch = useDispatch();

  React.useEffect(() => {
    if (!dbs) {
      dispatch(getUser());
    }
  }, [dbs, dispatch]);

  // Reset when modal opens/closes
  React.useEffect(() => {
    if (!open) {
      setStep(1);
      setColumns([]);
      setXColumn([]);
      setYColumn('');
      setPreviewData(null);
      setPrompt('');
      setTitle('New Visualization');
    }
  }, [open]);

  const handleNext = async () => {
    if (prompt === '') {
      window.alert('Please input the prompt.');
      return;
    }
    setLoading(true);
    try {
      const res = await visualizationService.setVisualization({ name: database, prompt });
      const { sql_query, result, id } = res.data;
      const cols = result && result.length > 0 ? Object.keys(result[0]) : [];
      setPreviewData({ id, sql_query, result });
      setColumns(cols);
      setXColumn(cols.slice(0, 1));
      setYColumn(cols[1] || '');
      setStep(2);
    } catch (err) {
      window.alert("Failed to fetch data. Please check your prompt and try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    if (!previewData) return;
    dispatch(addVisualizationGraph({
      id: previewData.id,
      title,
      type: 'bar',
      data: previewData.result,
      query: previewData.sql_query,
      prompt,
      db: database,
      x_column: xColumn,
      y_column: yColumn,
    }));
    handleClose();
  };

  return (
    <>
      <CDialog open={open} onClose={handleClose} maxWidth={'md'} fullWidth>
        <CDialogTitle>{step === 1 ? 'Visualization' : 'Select Chart Axes'}</CDialogTitle>
        <DialogContent>
          {step === 1 && (
            <>
              <CTextField
                id="title"
                label="Title"
                size="small"
                rows={3}
                variant="outlined"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                sx={{ marginTop: '1rem' }}
                fullWidth
              />
              <FormControl sx={{ marginTop: '1rem' }} size="small" fullWidth>
                <InputLabel id="db-label">Data Source</InputLabel>
                <Select
                  labelId="db-label"
                  value={database}
                  margin="dense"
                  label="Data Source"
                  onChange={(e) => setDatabase(e.target.value)}
                  fullWidth
                  variant="outlined"
                >
                  {dbs && dbs.length > 0 && dbs.map(item => (
                    <MenuItem key={item.name} value={item.name}>{item.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <CTextField
                id="prompt"
                label="Prompt"
                size="small"
                multiline
                rows={3}
                variant="outlined"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                sx={{ marginTop: '1rem' }}
                fullWidth
              />
            </>
          )}

          {step === 2 && (
            <>
              <Typography variant="body2" sx={{ marginTop: '1rem', marginBottom: '0.5rem', color: 'text.secondary' }}>
                Choose which columns to use for the chart axes.
              </Typography>
              <FormControl sx={{ marginTop: '1rem' }} size="small" fullWidth>
                <InputLabel id="x-col-label">X Axis (Categories)</InputLabel>
                <Select
                  labelId="x-col-label"
                  multiple
                  value={xColumn}
                  label="X Axis (Categories)"
                  onChange={(e) => setXColumn(e.target.value)}
                  renderValue={(selected) => selected.join(', ')}
                  fullWidth
                  variant="outlined"
                >
                  {columns.map(col => (
                    <MenuItem key={col} value={col}>{col}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl sx={{ marginTop: '1rem' }} size="small" fullWidth>
                <InputLabel id="y-col-label">Y Axis (Values)</InputLabel>
                <Select
                  labelId="y-col-label"
                  value={yColumn}
                  label="Y Axis (Values)"
                  onChange={(e) => setYColumn(e.target.value)}
                  fullWidth
                  variant="outlined"
                >
                  {columns.map(col => (
                    <MenuItem key={col} value={col}>{col}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}

          {loading && (
            <Box className="absolute w-full h-full" sx={{ top: 0, left: 0 }}>
              <Box className="relative w-full h-full">
                <Box className="w-full h-full flex justify-center align-center">
                  <CircularProgress sx={{ opacity: 0.7 }} />
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <CDialogActions>
          {step === 2 && (
            <CButton onClick={() => setStep(1)} disabled={loading}>Back</CButton>
          )}
          <CButton onClick={handleClose}>Cancel</CButton>
          {step === 1 && (
            <CButton onClick={handleNext} disabled={loading}>Next</CButton>
          )}
          {step === 2 && (
            <CButton onClick={handleSubmit} disabled={!xColumn.length || !yColumn}>Ok</CButton>
          )}
        </CDialogActions>
      </CDialog>
    </>
  );
}
